/**
 * KsbProfileTab — E2-S3 (thin): Trainee KSB profile & MOS-match view.
 *
 * Reads the trainee's attempts, joins with event KSB tags, rolls up into a
 * per-KSB attainment score, then renders a match/gap view against MOS profiles.
 *
 * Accept: for a seeded cohort, leadership can view "top MOS matches" per
 * trainee and "KSB gaps" per MOS.
 */

import { useState, useEffect } from 'react';
import { useRecordContext, useDataProvider } from 'react-admin';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import type { Student } from '../types';

// ---------------------------------------------------------------------------
// KSB label map (mirrors the KSB taxonomy seeded in E2-S1)
// ---------------------------------------------------------------------------

const KSB_LABELS: Record<string, string> = {
  'K':               'Knowledge (all)',
  'K-TCCC':          'TCCC Medical Knowledge',
  'K-WEAP':          'Weapons Systems Knowledge',
  'K-LAND-NAV':      'Land Navigation & Map Reading',
  'K-THREAT':        'Threat Recognition',
  'K-COMMS':         'Communications Procedures',
  'S':               'Skills (all)',
  'S-PHYS':          'Physical Fitness & Endurance',
  'S-MARKSMANSHIP':  'Marksmanship',
  'S-TCCC-SKILL':    'Tactical Casualty Care (Applied)',
  'S-LAND-NAV-SKILL':'Land Navigation (Applied)',
  'S-COMBATIVES':    'Army Combatives',
  'S-OBSTACLE':      'Obstacle & Confidence Course',
  'S-DEMO':          'Demolitions & Grenades',
  'B':               'Behaviors (all)',
  'B-LEAD':          'Leadership Under Stress',
  'B-TEAM':          'Team Cohesion & Cooperation',
  'B-ADAPT':         'Adaptability & Resilience',
  'B-DISC':          'Discipline & Standards Compliance',
  'B-INITIATIVE':    'Initiative & Mission Focus',
};

// ---------------------------------------------------------------------------
// Exemplar MOS / AOC profiles (thin — representative for demo)
// These are the "critical KSB requirements" from §5 E2-S2.
// ---------------------------------------------------------------------------

interface MosProfile {
  code: string;
  title: string;
  branch: string;
  requirements: { tag: string; minPct: number }[];
}

const MOS_PROFILES: MosProfile[] = [
  {
    code: '11B',
    title: 'Infantryman',
    branch: 'Infantry',
    requirements: [
      { tag: 'S-PHYS',        minPct: 75 },
      { tag: 'S-MARKSMANSHIP',minPct: 70 },
      { tag: 'S-COMBATIVES',  minPct: 60 },
      { tag: 'B-ADAPT',       minPct: 65 },
      { tag: 'B-DISC',        minPct: 65 },
      { tag: 'K-LAND-NAV',    minPct: 55 },
    ],
  },
  {
    code: '68W',
    title: 'Combat Medic Specialist',
    branch: 'Medical',
    requirements: [
      { tag: 'K-TCCC',        minPct: 80 },
      { tag: 'S-TCCC-SKILL',  minPct: 80 },
      { tag: 'S-PHYS',        minPct: 60 },
      { tag: 'B-TEAM',        minPct: 70 },
      { tag: 'B-ADAPT',       minPct: 65 },
    ],
  },
  {
    code: '11A',
    title: 'Infantry Officer (AOC)',
    branch: 'Infantry',
    requirements: [
      { tag: 'S-PHYS',        minPct: 80 },
      { tag: 'S-MARKSMANSHIP',minPct: 70 },
      { tag: 'B-LEAD',        minPct: 80 },
      { tag: 'B-INITIATIVE',  minPct: 75 },
      { tag: 'K-THREAT',      minPct: 65 },
      { tag: 'S-LAND-NAV-SKILL', minPct: 60 },
    ],
  },
  {
    code: '74D',
    title: 'Chemical Operations Specialist',
    branch: 'Chemical',
    requirements: [
      { tag: 'K-THREAT',      minPct: 75 },
      { tag: 'S-LAND-NAV-SKILL', minPct: 65 },
      { tag: 'B-DISC',        minPct: 75 },
      { tag: 'S-PHYS',        minPct: 60 },
      { tag: 'K-COMMS',       minPct: 60 },
    ],
  },
  {
    code: '25U',
    title: 'Signal Support Systems Specialist',
    branch: 'Signal',
    requirements: [
      { tag: 'K-COMMS',       minPct: 80 },
      { tag: 'B-DISC',        minPct: 70 },
      { tag: 'S-PHYS',        minPct: 55 },
      { tag: 'K-THREAT',      minPct: 55 },
    ],
  },
];

// ---------------------------------------------------------------------------
// Attempt + event types (minimal)
// ---------------------------------------------------------------------------

interface AttemptRecord {
  id: string;
  student_id?: string;
  event_id?: string;
  pass?: boolean;
  measurement_results?: { measurement_id: string; points?: number; raw_normalized?: number }[];
  [key: string]: unknown;
}

interface EventRecord {
  id: string;
  name: string;
  scoring_mode: string;
  ksb_tags?: string[];
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Attainment computation
// ---------------------------------------------------------------------------

interface KsbAttainment {
  tag: string;
  label: string;
  attainment: number;  // 0–100
  sampleSize: number;
}

function computeAttainment(
  attempts: AttemptRecord[],
  events: Map<string, EventRecord>,
): KsbAttainment[] {
  // Aggregate points per KSB tag
  const tagBuckets: Map<string, { sum: number; count: number }> = new Map();

  for (const attempt of attempts) {
    const ev = events.get(attempt.event_id ?? '');
    if (!ev?.ksb_tags?.length) continue;

    let score: number | null = null;

    if (ev.scoring_mode === 'pass_fail') {
      score = attempt.pass === true ? 100 : attempt.pass === false ? 0 : null;
    } else if (attempt.measurement_results?.length) {
      const pts = attempt.measurement_results
        .map((r) => r.points)
        .filter((p): p is number => typeof p === 'number');
      if (pts.length > 0) score = pts.reduce((a, b) => a + b, 0) / pts.length;
    }

    if (score === null) continue;

    // Normalize AFT 60-100 scale to 0-100 pct
    const normalizedPct = Math.min(100, Math.max(0, score));

    for (const tag of ev.ksb_tags) {
      const bucket = tagBuckets.get(tag) ?? { sum: 0, count: 0 };
      bucket.sum += normalizedPct;
      bucket.count += 1;
      tagBuckets.set(tag, bucket);
    }
  }

  return Array.from(tagBuckets.entries())
    .map(([tag, { sum, count }]) => ({
      tag,
      label: KSB_LABELS[tag] ?? tag,
      attainment: Math.round(sum / count),
      sampleSize: count,
    }))
    .sort((a, b) => b.attainment - a.attainment);
}

// Compute MOS match score: average pct of requirements met
function mosFit(profile: MosProfile, attainment: Map<string, number>): number {
  if (profile.requirements.length === 0) return 0;
  const scores = profile.requirements.map(({ tag, minPct }) => {
    const actual = attainment.get(tag) ?? 0;
    return Math.min(100, (actual / minPct) * 100);
  });
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

function getColor(pct: number): 'success' | 'warning' | 'error' {
  if (pct >= 75) return 'success';
  if (pct >= 50) return 'warning';
  return 'error';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function KsbProfileTab() {
  const record = useRecordContext<Student>();
  const dp = useDataProvider();

  const [attainment, setAttainment] = useState<KsbAttainment[]>([]);
  const [mosMatches, setMosMatches] = useState<{ profile: MosProfile; fit: number }[]>([]);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);

  useEffect(() => {
    if (!record?.id) return;
    setLoading(true);

    dp.getList('attempts', {
      pagination: { page: 1, perPage: 500 },
      sort: { field: 'id', order: 'ASC' },
      filter: { student_id: record.id },
    })
      .then(async (res) => {
        const attempts = res.data as AttemptRecord[];
        if (attempts.length === 0) { setNoData(true); setLoading(false); return; }

        // Load distinct events
        const eventIds = [...new Set(attempts.map((a) => a.event_id).filter(Boolean))] as string[];
        const evResults = await Promise.all(
          eventIds.map((eid) => dp.getOne('events', { id: eid }).catch(() => null)),
        );
        const eventMap = new Map<string, EventRecord>();
        for (const r of evResults) {
          if (r?.data) eventMap.set((r.data as EventRecord).id, r.data as EventRecord);
        }

        const atten = computeAttainment(attempts, eventMap);
        if (atten.length === 0) { setNoData(true); setLoading(false); return; }

        const attenMap = new Map(atten.map((a) => [a.tag, a.attainment]));
        const matches = MOS_PROFILES
          .map((p) => ({ profile: p, fit: mosFit(p, attenMap) }))
          .sort((a, b) => b.fit - a.fit);

        setAttainment(atten);
        setMosMatches(matches);
        setNoData(false);
      })
      .catch(() => setNoData(true))
      .finally(() => setLoading(false));
  }, [record?.id, dp]);

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  if (noData) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">
          No scored attempts found for this trainee. Score attempts (individually or via Batch Grade)
          to populate the KSB profile.
        </Alert>
      </Box>
    );
  }

  const attenMap = new Map(attainment.map((a) => [a.tag, a.attainment]));

  return (
    <Box sx={{ p: 1 }}>
      {/* KSB Attainment */}
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        KSB Attainment
      </Typography>
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>KSB Tag</TableCell>
              <TableCell>Domain</TableCell>
              <TableCell sx={{ width: 160 }}>Attainment</TableCell>
              <TableCell sx={{ width: 60 }}>Score</TableCell>
              <TableCell sx={{ width: 60 }}>n</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attainment.map((row) => {
              const color = getColor(row.attainment);
              return (
                <TableRow key={row.tag}>
                  <TableCell>
                    <Chip label={row.tag} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.label}</Typography>
                  </TableCell>
                  <TableCell>
                    <LinearProgress
                      variant="determinate"
                      value={row.attainment}
                      color={color}
                      sx={{ height: 8, borderRadius: 4 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={`${color}.main`} fontWeight="bold">
                      {row.attainment}%
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{row.sampleSize}</Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Divider sx={{ my: 2 }} />

      {/* MOS Match */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          MOS / AOC Match
        </Typography>
        <Chip label="Illustrative — demo data" size="small" color="warning" variant="outlined" />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Match score = average of (attained / required) across each MOS's critical KSBs.
      </Typography>

      {mosMatches.map(({ profile, fit }) => {
        const color = getColor(fit);
        return (
          <Paper key={profile.code} variant="outlined" sx={{ mb: 1, p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <Chip label={profile.code} size="small" color="primary" />
              <Typography variant="body2" fontWeight="bold">{profile.title}</Typography>
              <Typography variant="caption" color="text.secondary">({profile.branch})</Typography>
              <Box sx={{ flex: 1 }} />
              {fit >= 75
                ? <CheckCircleIcon fontSize="small" color="success" />
                : <WarningIcon fontSize="small" color={color} />}
              <Typography variant="body2" color={`${color}.main`} fontWeight="bold" sx={{ minWidth: 36, textAlign: 'right' }}>
                {fit}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={fit}
              color={color}
              sx={{ height: 6, borderRadius: 3, mb: 0.75 }}
            />
            {/* Gap detail */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {profile.requirements.map(({ tag, minPct }) => {
                const actual = attenMap.get(tag) ?? 0;
                const met = actual >= minPct;
                return (
                  <Tooltip
                    key={tag}
                    title={`${KSB_LABELS[tag] ?? tag}: ${actual}% attained, ${minPct}% required`}
                  >
                    <Chip
                      label={`${tag} ${actual}%/${minPct}%`}
                      size="small"
                      color={met ? 'success' : 'error'}
                      variant={met ? 'filled' : 'outlined'}
                    />
                  </Tooltip>
                );
              })}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
}
